import { createContext, useContext, useEffect, useReducer, useState } from 'react'
import { isEmpty, size } from 'lodash'
import { useDeepCompareEffect } from 'react-use'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { useDatasetsDataQueryObserver } from '@global/hooks/queries/useDatasetsDataQueryObserver'
import { reducer } from './reducer'
import {
  ActionType,
  Comments,
  ContextValues,
  CurrentCommentDetails,
  RemoveCommentArgs,
} from './types'
import { IDatasetRow } from '@global/api/transforms'

const initialState = {
  commentDialogOpen: false,
  comments: {},
  currentCommentDetails: null,
  rowsWithComments: [],
}

export const CommentsContext = createContext<ContextValues | null>(null)

export const CommentsContextProvider = ({ children, datasetId }) => {
  const { datasetLoading } = useDatasetQueryContext()
  const { data: datasetData } = useDatasetsDataQueryObserver({ datasetId })
  const [state, dispatch] = useReducer(reducer, initialState)
  const [rows, setRows] = useState<IDatasetRow[]>([])
  const { comments, rowsWithComments } = state
  const commentCount = size(comments)

  useEffect(() => {
    if (datasetLoading) return

    if (!datasetData?.rows) return

    setRows(datasetData.rows)
  }, [datasetLoading, datasetData?.rows])

  useDeepCompareEffect(() => {
    if (datasetLoading) return

    const readGridRows = getReadGridRows()

    setRows(readGridRows)

    function getReadGridRows() {
      if (datasetData?.rows) {
        if (commentCount === 0) {
          return datasetData.rows
        }

        return rowsWithComments
      }

      return []
    }
  }, [commentCount, datasetData?.rows, rowsWithComments])

  const isNotPropertyOrCvid = column =>
    column.governanceType !== 'property' && column.governanceType !== 'cv_id'

  const value = {
    ...state,
    rows,
    columns:
      datasetData?.filteredColumns?.filter(isNotPropertyOrCvid) ??
      datasetData?.allColumns?.filter(isNotPropertyOrCvid) ??
      [],
    commentCount,
    addComment,
    removeComment,
    dispatch,
  }
  return <CommentsContext.Provider value={value}>{children}</CommentsContext.Provider>

  /**
   * Adds comment to comments state object and calls a function to add the
   * comment to the corresponding dataset row.
   *
   * @param {CurrentCommentDetails} currentCommentDetails - Details of the current comment being added
   * @param {string} message - The text from the comment input
   */
  function addComment({
    currentCommentDetails,
    message,
  }: {
    currentCommentDetails: CurrentCommentDetails
    message: string
  }) {
    const { fieldId, cvId, commenter, rowIdx } = currentCommentDetails
    const updatedComments = { ...comments }

    if (updatedComments[cvId]) {
      updatedComments[cvId][fieldId] = { commenter, message }
    } else {
      updatedComments[cvId] = { [fieldId]: { commenter, message } }
    }

    addCellCommentAndUpdateRows({ cvId, fieldId, rowIdx, comments: updatedComments })
    dispatch({
      type: ActionType.setComments,
      payload: updatedComments,
    })
  }

  /**
   * Removes comment from comments state object and calls a function to remove the
   * comment from the corresponding dataset row.
   *
   * @param {string} cvId
   * @param {string} fieldId
   * @param {number} rowIdx
   */
  function removeComment({ cvId, fieldId, rowIdx }: RemoveCommentArgs) {
    let updatedComments = { ...comments }

    if (updatedComments[cvId]) {
      const { [Number(fieldId)]: _field, ...rest } = updatedComments[cvId]

      updatedComments[cvId] = rest
    }

    if (isEmpty(updatedComments[cvId])) {
      const { [cvId]: _cvId, ...rest } = updatedComments

      updatedComments = rest
    }

    removeCellCommentAndUpdateRows({ fieldId, rowIdx })
    dispatch({
      type: ActionType.setComments,
      payload: updatedComments,
    })
  }

  /**
   * Adds comment to the matching dataset row, and updates the rows in state.
   * This triggers a re-render of the table that is needed to display the updated rows.
   *
   * @param {string} cvId
   * @param {number | string} fieldId
   * @param {number} rowIdx
   * @param {Comments} comments - Updated comments object
   */
  function addCellCommentAndUpdateRows({
    cvId,
    fieldId,
    rowIdx,
    comments,
  }: {
    cvId: string
    fieldId: number | string
    rowIdx: number
    comments: Comments
  }) {
    const updatedRows = rows

    if (updatedRows[rowIdx]?.data[fieldId] && comments[cvId]?.[fieldId]) {
      updatedRows[rowIdx].data[fieldId].comment = {
        ...comments[cvId][fieldId],
      }
    }

    dispatch({
      type: ActionType.setRowsWithComments,
      payload: updatedRows,
    })
  }

  /**
   * Removes comment from the matching dataset row, and updates the rows in state.
   * This triggers a re-render of the table that is needed to display the updated rows.
   *
   * @param {number | string} fieldId
   * @param {number} rowIdx
   */
  function removeCellCommentAndUpdateRows({
    fieldId,
    rowIdx,
  }: {
    fieldId: number | string
    rowIdx: number
  }) {
    const updatedRows = rows

    if (updatedRows[rowIdx]?.data[fieldId]) {
      delete updatedRows[rowIdx].data[fieldId].comment
    }

    dispatch({
      type: ActionType.setRowsWithComments,
      payload: updatedRows,
    })
  }
}

/**
 * Simplified hook to be used in components.
 * Throws and error if used incorrectly and ensures context is not null.
 */
export const useCommentsContext = () => {
  const context = useContext(CommentsContext)

  if (!context) {
    throw new Error('useComments must be used within an CommentsContextProvider')
  }

  return context
}
